import { manualRefreshToken } from '@/services/refreshTokenService';
import axios from 'axios';
import { getSession, signOut } from 'next-auth/react';

const api = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  async (config) => {
    const session = await getSession();

    if (session?.error === 'RefreshAccessTokenError') {
      console.error('Session has refresh error, logging out...');

      if (session?.isTemporaryUser) {
        console.log('Temporary user session - continuing without refresh');
        return config;
      }

      await signOut({ redirect: true, callbackUrl: '/auth/login' });
      return Promise.reject(new Error('Session expired'));
    }

    const token = session?.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (response && response.status === 401 && !(config as { _retry?: boolean })?._retry) {
      (config as { _retry?: boolean })._retry = true;

      try {
        const session = await getSession();

        if (session?.error === 'RefreshAccessTokenError') {
          console.error('Refresh token is invalid, logging out...');
          if (session?.isTemporaryUser) {
            console.log('Temporary user session - continuing without refresh');
            return config;
          }
          await signOut({ redirect: true, callbackUrl: '/auth/login' });
          return Promise.reject(error);
        }

        const newAccessToken = await manualRefreshToken();

        if (!newAccessToken) {
          console.error('Token refresh failed, redirecting to login...');
          await signOut({ redirect: true, callbackUrl: '/auth/login' });
          return Promise.reject(error);
        }

        config.headers.Authorization = `Bearer ${newAccessToken}`;
        return api(config);
      } catch (refreshError) {
        console.error('Error during token refresh:', refreshError);
        await signOut({ redirect: true, callbackUrl: '/auth/login' });
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

interface ApiError {
  response?: {
    data?: {
      message?: string;
    };
  };
  message?: string;
}

export default api;
export type { ApiError };
